import type { Key } from "react-aria-components";

import { zodResolver } from "@hookform/resolvers/zod";
import { useId } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import {
  ClearableInput,
  FieldError,
  Label,
  TextField,
} from "@/components/field";
import { Modal } from "@/components/modal";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";

import { useInvoices } from "../../components/invoices/hooks/useInvoices";
import {
  CreditNoteField,
  CreditReasonSelect,
} from "./components/CreditReasonFields";
import { useIssueCredit } from "./hooks/useIssueCredit";
import {
  type IssueCreditFormInput,
  issueCreditFormSchema,
  type IssueCreditFormValues,
} from "./utils/issue-credit-schema";

const INVOICE_OPTIONS_LIMIT = 100;

interface IssueCreditModalProps {
  readonly invoiceId: string;
  readonly onClose: () => void;
  readonly projectId: number;
}

export function IssueCreditModal({
  invoiceId,
  onClose,
  projectId,
}: IssueCreditModalProps) {
  const formId = useId();
  const { data: invoices } = useInvoices({
    limit: INVOICE_OPTIONS_LIMIT,
    page: 1,
    projectId,
  });
  const invoiceOptions = invoices?.items.map(({ id }) => ({ id })) ?? [
    { id: invoiceId },
  ];
  const issueCredit = useIssueCredit(projectId);
  const { control, formState, handleSubmit, setValue } = useForm<
    IssueCreditFormInput,
    unknown,
    IssueCreditFormValues
  >({
    defaultValues: { amount: "", invoiceId, note: "" },
    mode: "onChange",
    resolver: zodResolver(issueCreditFormSchema),
  });

  const submit = (values: IssueCreditFormValues) =>
    issueCredit.mutate(values, { onSuccess: onClose });

  return (
    <Modal
      classNames={{ modal: "flex h-full w-full flex-col sm:h-auto sm:w-144" }}
      isDismissable
      isOpen
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <Dialog className="h-full">
        <DialogCloseButton />
        <DialogHeader className="px-xl pt-2xl text-lg font-semibold sm:border-b sm:border-border-secondary sm:px-3xl sm:pb-2xl sm:pt-3xl">
          Issue credit
        </DialogHeader>

        <DialogBody className="px-xl pt-xl sm:px-3xl sm:py-2xl">
          <form
            className="flex flex-col gap-xl"
            id={formId}
            onSubmit={handleSubmit(submit)}
          >
            <Controller
              control={control}
              name="invoiceId"
              render={({
                field: { onBlur, onChange, value },
                fieldState: { error, invalid },
              }) => (
                <Select
                  isInvalid={invalid}
                  onBlur={onBlur}
                  onSelectionChange={(key: Key | null) => onChange(key)}
                  placeholder="Select an invoice"
                  selectedKey={value}
                  validationBehavior="aria"
                >
                  <Label requiredHint>Invoice</Label>
                  <SelectButton />
                  <FieldError>{error?.message}</FieldError>
                  <SelectPopover>
                    <SelectListBox items={invoiceOptions}>
                      {(invoice) => (
                        <SelectListItem id={invoice.id}>
                          {invoice.id}
                        </SelectListItem>
                      )}
                    </SelectListBox>
                  </SelectPopover>
                </Select>
              )}
            />

            <Controller
              control={control}
              name="amount"
              render={({
                field: { onBlur, onChange, ref, value },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  isInvalid={invalid}
                  onBlur={onBlur}
                  onChange={onChange}
                  validationBehavior="aria"
                  value={value}
                >
                  <Label requiredHint>Amount</Label>
                  <ClearableInput
                    inputMode="decimal"
                    onClear={() =>
                      setValue("amount", "", { shouldValidate: true })
                    }
                    placeholder="$0.00"
                    ref={ref}
                    showClearButton={!!value}
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />

            <Controller
              control={control}
              name="reason"
              render={({ field, fieldState: { error } }) => (
                <CreditReasonSelect
                  error={error?.message}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  value={field.value}
                />
              )}
            />

            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <CreditNoteField
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  placeholder="Why this credit is being issued"
                  value={field.value}
                />
              )}
            />
          </form>
        </DialogBody>

        <DialogFooter className="px-xl pb-xl sm:border-t sm:border-border-secondary sm:px-3xl sm:pb-3xl">
          <Button
            className="sm:flex-1"
            color="gray"
            onPress={onClose}
            size="lg"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="sm:flex-1"
            form={formId}
            isDisabled={!formState.isValid || issueCredit.isPending}
            isLoading={issueCredit.isPending}
            size="lg"
            type="submit"
          >
            Issue credit
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
